import React, { useState } from 'react';
import { buildImageUrl } from '../../services/loucaApi';

/**
 * Composant pour afficher les phases LLM (Phase 1 et Phase 2)
 */
const LLMPhasesView = ({ result }) => {
  const [selectedPhase, setSelectedPhase] = useState('phase1');

  const phase1Input = result?.llm_input_phase1;
  const phase1Response = result?.raw_llm_response_phase1;
  const phase1Results = result?.v9_step1_results || [];

  const phase2Input = result?.llm_input_phase2;
  const phase2Response = result?.raw_llm_response_phase2;
  const phase2Results = result?.v9_step2_results || [];

  const parsePhase1Response = () => {
    if (!phase1Response) return [];
    try {
      if (typeof phase1Response === 'string') {
        return JSON.parse(phase1Response);
      }
      return Array.isArray(phase1Response) ? phase1Response : [phase1Response];
    } catch {
      return [];
    }
  };

  const parsePhase2Response = () => {
    if (!phase2Response) return [];
    try {
      if (typeof phase2Response === 'string') {
        return JSON.parse(phase2Response);
      }
      return Array.isArray(phase2Response) ? phase2Response : [phase2Response];
    } catch {
      return [];
    }
  };

  const phase1Data = parsePhase1Response();
  const phase2Data = parsePhase2Response();

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Phases LLM</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedPhase('phase1')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              selectedPhase === 'phase1'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Phase 1 (Détection)
          </button>
          <button
            onClick={() => setSelectedPhase('phase2')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              selectedPhase === 'phase2'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Phase 2 (Description)
          </button>
        </div>
      </div>

      {/* Phase 1 */}
      {selectedPhase === 'phase1' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-orange-500/20 to-slate-800 border border-orange-500/30 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-2">
              🔍 Phase 1: Détection de Dégâts
            </h3>
            <p className="text-sm text-slate-300">
              Modèle: {phase1Input?.model || 'gemini-2.5-flash-lite'}
            </p>
            <p className="text-sm text-slate-400">
              Images analysées: {phase1Input?.images?.length || 0}
            </p>
          </div>

          {phase1Input?.images && phase1Input.images.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-2">Images analysées</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {phase1Input.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700"
                  >
                    <img
                      src={buildImageUrl(img.path || img.url)}
                      alt={img.filename || `Image ${idx}`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-2 text-xs text-slate-300 truncate">
                      {img.filename || `Image ${idx}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase1Data.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-2">Résultats</h4>
              <div className="space-y-2">
                {phase1Data.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900 rounded-lg p-4 border border-slate-700"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">
                        {item.image_name || item.image_id}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.IsThereDamage === 'yes'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {item.IsThereDamage === 'yes' ? 'Dégât détecté' : 'Aucun dégât'}
                      </span>
                    </div>
                    {item.raw_response && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer">
                          Réponse brute
                        </summary>
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-x-auto">
                          {typeof item.raw_response === 'string'
                            ? item.raw_response
                            : JSON.stringify(item.raw_response, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase1Results.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-2">Résultats Step 1</h4>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <pre className="text-xs text-slate-300 overflow-x-auto">
                  {JSON.stringify(phase1Results, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 2 */}
      {selectedPhase === 'phase2' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-orange-500/20 to-slate-800 border border-orange-500/30 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-2">
              📝 Phase 2: Description Détaillée
            </h3>
            <p className="text-sm text-slate-300">
              Modèle: {phase2Input?.model || 'gemini-2.5-flash'}
            </p>
            <p className="text-sm text-slate-400">
              Images analysées: {phase2Input?.images?.length || 0}
            </p>
          </div>

          {phase2Input?.images && phase2Input.images.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-2">Images avec grille analysées</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {phase2Input.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700"
                  >
                    <img
                      src={buildImageUrl(img.path || img.url || img.grid_path)}
                      alt={img.filename || `Image ${idx}`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-2 text-xs text-slate-300 truncate">
                      {img.filename || `Image ${idx}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase2Data.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-2">Résultats détaillés</h4>
              <div className="space-y-4">
                {phase2Data.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900 rounded-lg p-4 border border-slate-700"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-medium">
                        {item.image_name || item.image_id}
                      </span>
                      {item.car_side && (
                        <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-xs">
                          {item.car_side}
                        </span>
                      )}
                    </div>

                    {item.damages && item.damages.length > 0 && (
                      <div className="space-y-2">
                        {item.damages.map((damage, dIdx) => (
                          <div
                            key={dIdx}
                            className="bg-slate-950 rounded-lg p-3 border border-slate-800"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white font-medium">{damage.damage_type}</span>
                              <div className="flex gap-2">
                                <span className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300">
                                  {damage.severity}
                                </span>
                                {damage.confidence && (
                                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                                    {Math.round(parseFloat(damage.confidence) * 100)}%
                                  </span>
                                )}
                              </div>
                            </div>
                            {damage.coordinates && (
                              <p className="text-xs text-orange-300 mb-1">
                                Coordonnées: {damage.coordinates}
                              </p>
                            )}
                            {damage.description && (
                              <p className="text-sm text-slate-300">{damage.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {item.raw_response && (
                      <details className="mt-3">
                        <summary className="text-xs text-slate-400 cursor-pointer">
                          Réponse brute
                        </summary>
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-x-auto">
                          {typeof item.raw_response === 'string'
                            ? item.raw_response
                            : JSON.stringify(item.raw_response, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase2Results.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-2">Résultats Step 2</h4>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <pre className="text-xs text-slate-300 overflow-x-auto">
                  {JSON.stringify(phase2Results, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LLMPhasesView;



